/**
 * SendPanel — persistent right column of the v2 Fax workspace.
 *
 * Per PART 7:
 *   - recipient search over provider directory, chip when selected
 *   - document drop zone that also accepts "pick from a chart"
 *   - cover-sheet selector (toggle + editable fields)
 *   - gold "Send fax" primary
 *   - Below: IN PROGRESS live per-fax status with failure reasons
 *     and retry attempts.
 *
 * Uses existing endpoints only — no send-logic change (PART 0 preamble).
 * Multipart POST to /api/fax/send matches the legacy Fax.jsx contract.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Send, Upload, Search, X, Loader2, FileText, RotateCcw, AlertTriangle, CheckCircle2,
  ChevronDown, ChevronRight, ImageIcon,
} from "lucide-react";
import { contactsAPI } from "../../../services/api";
import { faxAPI } from "./faxAPI";
import { formatPhoneNumber, toErrorMessage } from "../../../lib/utils";
import { NAVY, GOLD, GREEN, RED, NAVY_HAIRLINE, NAVY_TINT } from "../tokens";

/* ── Recipient search — filters existing contact directory ── */
function RecipientSearch({ selected, setSelected }) {
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await contactsAPI.getAll();
        if (alive) setContacts((r.data || []).filter(c => c.fax_number));
      } catch { /* directory unavailable → show empty */ }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return contacts.slice(0, 20);
    const term = q.trim().toLowerCase();
    return contacts.filter(c => {
      const name = `${c.first_name || ""} ${c.last_name || ""} ${c.organization || ""}`.toLowerCase();
      const fax = (c.fax_number || "").replace(/\D/g, "");
      return name.includes(term) || fax.includes(term.replace(/\D/g, ""));
    }).slice(0, 20);
  }, [contacts, q]);

  if (selected) {
    return (
      <div className="flex items-center gap-2 rounded-md p-2" style={{ background: NAVY_TINT, border: `1px solid ${NAVY_HAIRLINE}` }} data-testid="comms2-fax-recipient-chip">
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] font-semibold truncate" style={{ color: NAVY }}>
            {selected.first_name || ""} {selected.last_name || selected.organization || ""}
          </div>
          <div className="text-[10.5px] text-slate-500 font-mono">{formatPhoneNumber(selected.fax_number)}</div>
        </div>
        <button
          onClick={() => setSelected(null)}
          className="w-6 h-6 rounded hover:bg-white/60 flex items-center justify-center"
          data-testid="comms2-fax-recipient-clear"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="flex items-center gap-1.5 rounded-md border h-9 px-2" style={{ borderColor: NAVY_HAIRLINE }}>
        <Search className="w-3.5 h-3.5 opacity-60" style={{ color: NAVY }} />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Search provider directory"
          className="flex-1 text-[12.5px] bg-transparent outline-none"
          data-testid="comms2-fax-recipient-search"
        />
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-white border rounded-md shadow-lg z-30" style={{ borderColor: NAVY_HAIRLINE }}>
          {filtered.map(c => (
            <button
              key={c.id}
              onClick={() => { setSelected(c); setQ(""); setOpen(false); }}
              className="w-full text-left px-2 py-1.5 hover:bg-slate-50 flex items-center gap-2"
              data-testid={`comms2-fax-recipient-${c.id}`}
            >
              <span className="text-[12px] font-medium truncate flex-1" style={{ color: NAVY }}>
                {c.first_name} {c.last_name || c.organization}
              </span>
              <span className="text-[10.5px] font-mono opacity-70">{formatPhoneNumber(c.fax_number)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Document drop zone ── */
function DropZone({ file, setFile }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const pick = (f) => {
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) { toast.error("File too large (25MB max)"); return; }
    setFile(f);
  };

  if (file) {
    return (
      <div className="flex items-center gap-2 rounded-md p-2" style={{ background: NAVY_TINT, border: `1px solid ${NAVY_HAIRLINE}` }} data-testid="comms2-fax-file-chip">
        <FileText className="w-4 h-4 shrink-0" style={{ color: NAVY }} />
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-medium truncate" style={{ color: NAVY }}>{file.name}</div>
          <div className="text-[10.5px] text-slate-500">{(file.size / 1024).toFixed(0)} KB</div>
        </div>
        <button
          onClick={() => setFile(null)}
          className="w-6 h-6 rounded hover:bg-white/60 flex items-center justify-center"
          data-testid="comms2-fax-file-clear"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files[0]); }}
      className="rounded-md border-2 border-dashed py-4 px-2 text-center cursor-pointer transition-colors"
      style={{ borderColor: dragging ? NAVY : NAVY_HAIRLINE, background: dragging ? NAVY_TINT : "transparent" }}
      data-testid="comms2-fax-dropzone"
    >
      <Upload className="w-4 h-4 mx-auto opacity-60 mb-1" style={{ color: NAVY }} />
      <div className="text-[11.5px] font-medium" style={{ color: NAVY }}>Drop a PDF or click to pick</div>
      <div className="text-[10px] text-slate-500">PDF · DOC · DOCX · JPG · PNG · 25MB max</div>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        className="hidden"
        onChange={(e) => pick(e.target.files[0])}
        data-testid="comms2-fax-file-input"
      />
    </div>
  );
}

/* ── Live in-progress + retry-queue tail ── */
function InProgressPanel({ tick }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Retry queue = the "failed / retrying" bucket. Combined with the
        // sent list's newest 5 pending items to give a live view.
        const [rq, sent] = await Promise.allSettled([faxAPI.retryQueue(), faxAPI.listSent(10)]);
        const rqItems = rq.status === "fulfilled" ? (rq.value?.items || rq.value?.faxes || []) : [];
        const sentItems = sent.status === "fulfilled" ? (Array.isArray(sent.value) ? sent.value : sent.value?.items || []) : [];
        const pending = sentItems.filter(f => ["queued", "sending", "in_progress", "pending"].includes(f.status || ""));
        if (!alive) return;
        setItems([...pending, ...rqItems].slice(0, 6));
      } catch { /* empty state below */ }
    })();
    return () => { alive = false; };
  }, [tick]);

  if (items.length === 0) return null;
  return (
    <div className="border-t p-3 space-y-1.5" style={{ borderColor: NAVY_HAIRLINE }} data-testid="comms2-fax-in-progress">
      <div className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: NAVY }}>In progress</div>
      {items.map(f => {
        const failed = ["failed", "error", "no-answer", "busy"].includes(f.status);
        const sending = ["queued", "sending", "in_progress", "pending"].includes(f.status);
        return (
          <div key={f.id} className="text-[11px] rounded-md px-2 py-1.5" style={{ background: failed ? "rgba(192,57,43,0.06)" : NAVY_TINT }}>
            <div className="flex items-center gap-1.5">
              {sending && <Loader2 className="w-3 h-3 animate-spin" style={{ color: NAVY }} />}
              {failed && <AlertTriangle className="w-3 h-3" style={{ color: RED }} />}
              {!failed && !sending && <CheckCircle2 className="w-3 h-3" style={{ color: GREEN }} />}
              <span className="font-semibold truncate" style={{ color: NAVY }}>
                {f.to_name || f.to_number || formatPhoneNumber(f.to_number || "") || "Unknown"}
              </span>
              <span className="ml-auto text-slate-500 tabular-nums text-[10px]">{f.status || "?"}</span>
            </div>
            {failed && (
              <div className="text-[10.5px] text-slate-600 mt-0.5">
                {f.error_message || f.failure_reason || "unknown reason"}
                {typeof f.retry_attempt === "number" && ` · retry ${f.retry_attempt}/${f.max_retries || 3}`}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function SendPanel() {
  const [recipient, setRecipient] = useState(null);
  const [file, setFile] = useState(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [includeCover, setIncludeCover] = useState(true);
  const [sending, setSending] = useState(false);
  const [tick, setTick] = useState(0); // re-poll in-progress after send

  // Cover-sheet detail overrides — expandable accordion (PART 7 spec:
  // "cover-sheet selector"). Backend accepts from_name, to_phone, cc,
  // cover_pages as optional multipart fields on /api/fax/send; when
  // blank the backend fills sensible defaults (agency name, recipient
  // fax as to-phone, empty CC, auto page count). Details panel stays
  // collapsed by default so the "obvious" send flow stays quick.
  const [showCoverDetails, setShowCoverDetails] = useState(false);
  const [coverFromName, setCoverFromName] = useState("");
  const [coverToPhone, setCoverToPhone]   = useState("");
  const [coverCC, setCoverCC]             = useState("");
  const [coverPages, setCoverPages]       = useState("");
  const coverEdited = !!(coverFromName || coverToPhone || coverCC || coverPages);

  // Live cover-sheet preview — debounced 600 ms so keystrokes don't
  // flood the renderer. Fires whenever the accordion is expanded AND
  // the cover-toggle is on. Fetches a PNG blob from
  // /api/fax/cover-sheet/preview?as=image and renders it as an <img>.
  // Failures are swallowed to a small "Preview unavailable" hint —
  // it's a nicety, not a requirement, and pdf2image can throw when
  // poppler is missing on the host.
  const [previewUrl, setPreviewUrl]   = useState("");
  const [previewErr, setPreviewErr]   = useState("");
  const [previewBusy, setPreviewBusy] = useState(false);
  const previewTimer = useRef(null);
  const previewController = useRef(null);
  useEffect(() => {
    // Only render when the accordion is open and cover-sheet is on.
    if (!includeCover || !showCoverDetails) return;
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      if (previewController.current) previewController.current.abort();
      previewController.current = new AbortController();
      setPreviewBusy(true); setPreviewErr("");
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/fax/cover-sheet/preview?as=image`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
          },
          body: JSON.stringify({
            to_name: recipient ? `${recipient.first_name || ""} ${recipient.last_name || recipient.organization || ""}`.trim() : "",
            to_fax: recipient?.fax_number || "",
            to_phone: coverToPhone,
            from_name: coverFromName,
            subject, message,
            pages: coverPages,
            cc: coverCC,
          }),
          signal: previewController.current.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        // Revoke old blob URL before assigning new one to avoid leaks.
        setPreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); });
      } catch (e) {
        if (e.name === "AbortError") return; // superseded, silent
        setPreviewErr("Preview unavailable");
      } finally { setPreviewBusy(false); }
    }, 600);
    return () => { if (previewTimer.current) clearTimeout(previewTimer.current); };
  }, [includeCover, showCoverDetails, coverFromName, coverToPhone, coverCC, coverPages, subject, message, recipient]);
  // Clean up any lingering blob URL when the panel unmounts.
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  // Poll in-progress list every 8 seconds so failures / status changes
  // surface without a manual refresh.
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 8000);
    return () => clearInterval(t);
  }, []);

  const canSend = !!recipient && !!file && !sending;

  const doSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Backend expects `recipient` (E.164 fax destination) — not to_number.
      fd.append("recipient", recipient.fax_number);
      fd.append("to_name", `${recipient.first_name || ""} ${recipient.last_name || recipient.organization || ""}`.trim());
      fd.append("subject", subject);
      fd.append("message", message);
      fd.append("include_coversheet", includeCover ? "true" : "false");
      // Cover-sheet overrides — only appended when the admin actually
      // typed something so blank fields don't overwrite backend defaults
      // (agency name, recipient fax as to-phone, empty CC, auto pages).
      if (coverFromName.trim()) fd.append("from_name", coverFromName.trim());
      if (coverToPhone.trim())  fd.append("to_phone",  coverToPhone.trim());
      if (coverCC.trim())       fd.append("cc",        coverCC.trim());
      if (coverPages.trim())    fd.append("cover_pages", coverPages.trim());
      await faxAPI.send(fd);
      toast.success("Fax queued");
      setFile(null); setSubject(""); setMessage("");
      // Reset cover-sheet overrides too so next fax to a different
      // provider doesn't inherit the last customization.
      setCoverFromName(""); setCoverToPhone(""); setCoverCC(""); setCoverPages("");
      setShowCoverDetails(false);
      // Keep the recipient — same provider often gets more than one fax.
      setTick(x => x + 1);
    } catch (e) {
      toast.error(toErrorMessage(e, "Send failed"));
    } finally { setSending(false); }
  };

  return (
    <aside className="hidden md:flex w-[300px] shrink-0 border-l bg-white overflow-y-auto flex-col" style={{ borderColor: NAVY_HAIRLINE }} data-testid="comms2-fax-send-panel">
      <div className="p-3 border-b" style={{ borderColor: NAVY_HAIRLINE }}>
        <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: NAVY }}>Send fax</div>
      </div>
      <div className="p-3 space-y-3 flex-1">
        <div>
          <label className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-1 block">Recipient</label>
          <RecipientSearch selected={recipient} setSelected={setRecipient} />
        </div>
        <div>
          <label className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-1 block">Document</label>
          <DropZone file={file} setFile={setFile} />
        </div>
        <div>
          <label className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-1 block">Subject</label>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full h-8 px-2 rounded-md border text-[12.5px] outline-none"
            style={{ borderColor: NAVY_HAIRLINE }}
            data-testid="comms2-fax-subject"
          />
        </div>
        <div>
          <label className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-1 block">Note</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            className="w-full px-2 py-1.5 rounded-md border text-[12.5px] outline-none resize-none"
            style={{ borderColor: NAVY_HAIRLINE }}
            data-testid="comms2-fax-note"
          />
        </div>
        <label className="flex items-center gap-2 text-[12px] cursor-pointer" data-testid="comms2-fax-cover-toggle">
          <input type="checkbox" checked={includeCover} onChange={(e) => setIncludeCover(e.target.checked)} />
          <span style={{ color: NAVY }}>Include Avida cover sheet</span>
        </label>

        {/* Cover-sheet detail overrides — expandable accordion. Kept
            collapsed by default so the quick-send flow stays uncluttered.
            Disabled entirely (with an explanatory hint) when the user
            has opted out of the cover sheet. */}
        {includeCover && (
          <div className="rounded-md border" style={{ borderColor: NAVY_HAIRLINE }} data-testid="comms2-fax-cover-details">
            <button
              type="button"
              onClick={() => setShowCoverDetails(v => !v)}
              className="w-full flex items-center gap-1.5 h-8 px-2 text-[12px] font-medium hover:bg-slate-50"
              style={{ color: NAVY }}
              data-testid="comms2-fax-cover-details-toggle"
              aria-expanded={showCoverDetails}
            >
              {showCoverDetails ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              <span>Cover sheet details</span>
              {coverEdited && !showCoverDetails && (
                <span className="ml-1 text-[9.5px] font-bold px-1.5 py-px rounded" style={{ background: NAVY_TINT, color: NAVY }}>
                  overridden
                </span>
              )}
              <div className="flex-1" />
              {coverEdited && (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    setCoverFromName(""); setCoverToPhone(""); setCoverCC(""); setCoverPages("");
                  }}
                  className="text-[10.5px] text-slate-500 hover:text-slate-800 underline"
                  role="button"
                  tabIndex={0}
                  data-testid="comms2-fax-cover-reset"
                >
                  reset
                </span>
              )}
            </button>
            {showCoverDetails && (
              <div className="p-2 pt-1 border-t space-y-2" style={{ borderColor: NAVY_HAIRLINE }}>
                <div>
                  <label className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500 block mb-0.5">From name</label>
                  <input
                    value={coverFromName}
                    onChange={(e) => setCoverFromName(e.target.value)}
                    placeholder="Avida Personal Care"
                    className="w-full h-8 px-2 rounded-md border text-[12px] outline-none"
                    style={{ borderColor: NAVY_HAIRLINE }}
                    data-testid="comms2-fax-cover-from-name"
                  />
                </div>
                <div>
                  <label className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500 block mb-0.5">
                    To phone <span className="text-slate-400 font-normal">(voice number, not fax)</span>
                  </label>
                  <input
                    value={coverToPhone}
                    onChange={(e) => setCoverToPhone(e.target.value)}
                    placeholder="(555) 555-1234"
                    className="w-full h-8 px-2 rounded-md border text-[12px] outline-none"
                    style={{ borderColor: NAVY_HAIRLINE }}
                    data-testid="comms2-fax-cover-to-phone"
                  />
                </div>
                <div>
                  <label className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500 block mb-0.5">CC</label>
                  <input
                    value={coverCC}
                    onChange={(e) => setCoverCC(e.target.value)}
                    placeholder="Names or numbers copied on this fax"
                    className="w-full h-8 px-2 rounded-md border text-[12px] outline-none"
                    style={{ borderColor: NAVY_HAIRLINE }}
                    data-testid="comms2-fax-cover-cc"
                  />
                </div>
                <div>
                  <label className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500 block mb-0.5">
                    Pages <span className="text-slate-400 font-normal">(leave blank = auto)</span>
                  </label>
                  <input
                    value={coverPages}
                    onChange={(e) => setCoverPages(e.target.value)}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="e.g. 3"
                    className="w-full h-8 px-2 rounded-md border text-[12px] outline-none"
                    style={{ borderColor: NAVY_HAIRLINE }}
                    data-testid="comms2-fax-cover-pages"
                  />
                </div>

                {/* Live preview thumbnail — re-renders 600ms after any
                    accordion field, subject, note, or recipient changes.
                    Endpoint: POST /api/fax/cover-sheet/preview?as=image */}
                <div className="pt-1">
                  <div className="flex items-center gap-1.5 mb-1">
                    <ImageIcon className="w-3 h-3" style={{ color: NAVY }} />
                    <span className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500">Live preview</span>
                    {previewBusy && <Loader2 className="w-3 h-3 animate-spin ml-auto" style={{ color: NAVY }} />}
                  </div>
                  <div
                    className="rounded-md border overflow-hidden flex items-center justify-center bg-slate-50"
                    style={{ borderColor: NAVY_HAIRLINE, minHeight: 140 }}
                    data-testid="comms2-fax-cover-preview"
                  >
                    {previewErr && (
                      <div className="text-[10.5px] text-slate-400 italic px-2 py-4 text-center">
                        {previewErr} — the fax still sends fine
                      </div>
                    )}
                    {!previewErr && previewUrl && (
                      <img
                        src={previewUrl}
                        alt="Cover sheet"
                        className="w-full h-auto max-h-[280px] object-contain"
                        data-testid="comms2-fax-cover-preview-img"
                      />
                    )}
                    {!previewErr && !previewUrl && !previewBusy && (
                      <div className="text-[10.5px] text-slate-400 italic px-2 py-4 text-center">
                        Waiting for first render…
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <button
          onClick={doSend}
          disabled={!canSend}
          className="w-full h-10 rounded-lg text-[13.5px] font-bold flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed transition"
          style={{ background: GOLD, color: NAVY }}
          data-testid="comms2-fax-send"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {sending ? "Sending…" : "Send fax"}
        </button>
      </div>
      <InProgressPanel tick={tick} />
    </aside>
  );
}
